import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ClipboardList, Search, Calendar, User, MapPin, CheckCircle, AlertCircle, Clock, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '@/contexts/SettingsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RESPONSIBLE_LABELS = {
  'dj': 'DJ / Owner',
  'employee': 'Employee',
  'client': 'Client'
};

export default function TasksPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const navigate = useNavigate();
  const { formatDate } = useSettings();
  
  const [tasksData, setTasksData] = useState({ tasks: [], overdue_count: 0, due_today_count: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all'); // 'all', 'overdue', 'today', 'upcoming'

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/tasks/due`);
      setTasksData(response.data || { tasks: [], overdue_count: 0, due_today_count: 0 });
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteTask = async (task) => {
    try {
      await axios.put(`${API}/bookings/${task.booking_id}/tasks/${task.id}/complete`);
      toast.success('Task completed');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to complete task');
    }
  };

  const today = new Date().toISOString().split('T')[0];

  // Filter tasks
  const filteredTasks = tasksData.tasks.filter(task => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!task.name?.toLowerCase().includes(query) &&
          !task.client_name?.toLowerCase().includes(query) &&
          !task.event_type?.toLowerCase().includes(query)) {
        return false;
      }
    }
    
    // Status filter
    if (filter === 'overdue') return task.is_overdue;
    if (filter === 'today') return task.due_date === today;
    if (filter === 'upcoming') return task.due_date > today;
    
    return true;
  });

  const getStatusBadge = (task) => {
    if (task.status === 'completed') {
      return <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-500">Completed</span>;
    }
    if (task.is_overdue) {
      return <span className="px-2 py-0.5 text-xs rounded-full bg-red-500/20 text-red-500">Overdue</span>;
    }
    if (task.due_date === today) {
      return <span className="px-2 py-0.5 text-xs rounded-full bg-orange-500/20 text-orange-500">Due Today</span>;
    }
    return <span className="px-2 py-0.5 text-xs rounded-full bg-neon-blue/20 text-neon-blue">Upcoming</span>;
  };

  return (
    <Layout user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo}>
      <div data-testid="tasks-page">
        <div className="p-4 md:p-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="font-heading text-4xl font-bold mb-2" data-testid="tasks-heading">
                Tasks
                {(tasksData.overdue_count > 0 || tasksData.due_today_count > 0) && (
                  <span className="ml-3 text-lg font-normal">
                    {tasksData.overdue_count > 0 && (
                      <span className="text-red-500">{tasksData.overdue_count} overdue</span>
                    )}
                    {tasksData.overdue_count > 0 && tasksData.due_today_count > 0 && ' • '}
                    {tasksData.due_today_count > 0 && (
                      <span className="text-orange-500">{tasksData.due_today_count} due today</span>
                    )}
                  </span>
                )}
              </h1>
              <p className="text-muted-foreground">Track tasks across all your bookings</p>
            </div>
          </div>

          {/* Search and Filter */}
          <div className="flex gap-4 mb-6 flex-wrap">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-muted/20 border-glass-border"
                data-testid="search-tasks-input"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={filter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('all')}
                className={filter === 'all' ? 'bg-neon-blue hover:bg-neon-blue/90' : 'border-glass-border'}
              >
                All ({tasksData.tasks.length})
              </Button>
              <Button
                variant={filter === 'overdue' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('overdue')}
                className={filter === 'overdue' ? 'bg-red-500 hover:bg-red-500/90' : 'border-glass-border text-red-500'}
              >
                Overdue ({tasksData.overdue_count})
              </Button>
              <Button
                variant={filter === 'today' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('today')}
                className={filter === 'today' ? 'bg-orange-500 hover:bg-orange-500/90' : 'border-glass-border text-orange-500'}
              >
                Due Today ({tasksData.due_today_count})
              </Button>
              <Button
                variant={filter === 'upcoming' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('upcoming')}
                className={filter === 'upcoming' ? 'bg-neon-blue hover:bg-neon-blue/90' : 'border-glass-border'}
              >
                Upcoming
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">Loading tasks...</div>
          ) : filteredTasks.length === 0 ? (
            <Card className="glass-card border-glass-border">
              <CardContent className="py-16 text-center">
                <ClipboardList className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-xl font-semibold mb-2">No Tasks</h3>
                <p className="text-muted-foreground">
                  {filter === 'all' 
                    ? "No tasks due in the next 7 days. You're all caught up!" 
                    : `No ${filter} tasks found.`}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredTasks.map((task) => (
                <Card 
                  key={task.id} 
                  className={`glass-card border-glass-border hover:border-neon-blue/50 transition-colors ${task.is_overdue ? 'border-red-500/30' : ''}`}
                  data-testid={`task-${task.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleCompleteTask(task)}
                        className={`rounded-full border-2 ${task.is_overdue ? 'border-red-500 hover:bg-red-500/20' : 'border-glass-border hover:bg-neon-blue/20'}`}
                        data-testid={`complete-task-${task.id}`}
                      >
                        <CheckCircle className={`w-5 h-5 ${task.is_overdue ? 'text-red-500' : 'text-muted-foreground'}`} />
                      </Button>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-semibold">{task.name}</h3>
                          {getStatusBadge(task)}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <User className="w-3.5 h-3.5" />
                            {task.client_name || 'Unknown Client'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            Due: {formatDate(task.due_date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            Event: {formatDate(task.event_date)}
                          </span>
                          {task.venue && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3.5 h-3.5" />
                              {task.venue}
                            </span>
                          )}
                          <span className="px-2 py-0.5 rounded-full bg-muted/50 text-xs">
                            {RESPONSIBLE_LABELS[task.responsible] || task.responsible}
                          </span>
                        </div>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/bookings?open=${task.booking_id}&tab=tasks`)}
                        className="text-muted-foreground hover:text-foreground"
                        data-testid={`view-booking-${task.id}`}
                      >
                        View Booking
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
